import { UIElement } from '../../../shared-components/components/ui-element';
import { Component } from '../../../shared-components/decorators/component.decorator';
import { Attribute } from '../../../shared-components/decorators/attribute.decorator';
import {
    createElement,
    getBaseGridPoint,
    whenDOMReady,
} from '../../../shared-components/global/ui-helpers';
import { UIIcon } from '../../../shared-components/components/icon/ui-icon';
import type { AttributeValue } from '../../../shared-components/components/ui-element.type';
// TODO: GUI-4447 after migration from karma module resolution should be simplified. Karma task done on 09.09.2026, GUI-4447 can be taken into work.
import Highcharts from '../../runtime/highcharts-runtime';
import type {
    Series,
    Point,
    Options,
    LegendOptions,
    SeriesOptionsType,
    EventCallbackFunction,
    EventOptionsObject,
    AnimationOptionsObject,
    Chart,
} from 'highcharts';
import {
    LegendFormatterContext,
    UIChartExtendedOptions,
} from '../charts.types';

import styles from './ui-chart.css';

// Highcharts ships as a UMD bundle — named ESM exports are not available at
// runtime. Bind to the namespace so `this` is correct inside the callee.
const merge = Highcharts.merge.bind(Highcharts);
const addEvent = Highcharts.addEvent.bind(Highcharts);

/**
 * @class
 * @memberof ChartsComponents
 * @abstract
 * @element ui-chart
 * @augments {UIElement}
 * @alias UIChart
 * @classdesc Represents a class for <code>ui-chart</code> element.
 * This is a basic abstract class for chart component. It has
 * main methods to work with charts. Can have children:
 * &lt;ui-chart-options>, &lt;ui-chart-series>.
 * as child elements and can be passed all possible Highchart API options.
 * @property {string} [units=""] {@attr units} Units of the chart labels
 *  like EUR, %, pcs, $ etc.
 * @property {("static" | "dynamic" | "interactive")} [preset="static"] {@attr preset}
 *  Presets with different options, check ui-chart-options.
 *  {@desc static: chart without any animation}
 *  {@desc dynamic: basic animations are added}
 *  {@desc interactive: fully animatable and clickable chart with rich features}
 * Chart renders only if it is visible on viewport.
 * @property {number} [height=400] {@attr height} Height of the chart.
 * @property {boolean} [hideTitle] {@attr hide-title} Hide title from visual representation.
 * NB: this will hide title only visual, aria-label attribute with title still will exist.
 * @property {("area" | "areaspline" | "bar" | "column" | "line" | "lineargauge" | "pie" |
 *             "solidgauge" | "spline" )} [type="line"] {@attr type} Type of the chart.
 * {@desc area: Area chart}
 * {@desc areaspline: Area-spline chart}
 * {@desc bar: Bar chart}
 * {@desc line: Line chart}
 * {@desc lineargauge: Linear gauge chart}
 * {@desc pie: Pie chart}
 * {@desc solidgauge: Solid gauge chart}
 * {@desc spline: Spline chart}
 * @slot {@type "ui-series"}
 * @example
 * <ui-chart preset="interactive" type="bar">
 *   <ui-series data="[10, 25, 50, 33, 43]"></ui-series>
 * </ui-chart>
 */
@Component({ tag: 'ui-chart', styles })
export class UIChart extends UIElement {
    @Attribute(String) units: string = '';
    @Attribute(String) preset: string = 'dynamic';
    @Attribute(Number) height: number = 400;
    @Attribute(Boolean) hideTitle?: boolean;

    protected opts: Options = {};
    protected _container?: HTMLElement;
    protected _chart!: Chart;

    static get observedAttributes(): Array<string> {
        return ['units', 'preset', 'height', 'hide-title'];
    }

    override observeAttributes(
        name: string,
        oldValue: AttributeValue,
        newValue: string | null
    ): void {
        /* istanbul ignore if */
        if (!this.hydrated) {
            return;
        }
        switch (name) {
            case 'units':
            case 'preset':
            case 'height':
            case 'hide-title':
                this.renderChart();
                break;
            default:
                break;
        }
    }

    get type(): string {
        return this.getAttribute('type') ?? 'line';
    }

    set type(value: string) {
        this.setAttribute('type', value);
    }

    get stock(): boolean {
        return false;
    }

    get container(): HTMLElement {
        if (!this._container) {
            this._container = this.createContainer();
        }
        return this._container;
    }

    /**
     * @returns {UIChart}
     */
    get instance(): UIChart {
        return this;
    }

    get chart(): Chart {
        // Danger! Check this very-very carefully. It might have infinite loop.
        if (!this._chart) {
            this.renderChart();
        }
        return this._chart;
    }

    /**
     * Numbers for colors
     */
    static readonly COLORS_COUNT = 17;

    private static _gridBasepoint: number | undefined;

    static get BaseGrid(): number {
        if (!UIChart._gridBasepoint) {
            UIChart._gridBasepoint = getBaseGridPoint();
        }
        return UIChart._gridBasepoint;
    }

    private static get _a11yConfig(): Options {
        return {
            accessibility: {
                keyboardNavigation: {
                    focusBorder: {
                        enabled: false, // reset highcharts specific focus and use global.
                        hideBrowserFocusOutline: false, // <...>
                        style: {},
                    },
                },
            },
        };
    }

    /**
     * Predefined presets for charts.
     * <ul class="regular-list">
     *   <li><b>static</b> - chart without any animation;</li>
     *   <li><b>dynamic</b> - basic animations are added;</li>
     *   <li><b>interactive</b> - fully animatable and clickable chart with rich features.</li>
     * </ul>
     */
    static get presets(): Record<string, Options> {
        return {
            static: {
                ...UIChart._a11yConfig,
                chart: {
                    animation: false,
                    zooming: {
                        mouseWheel: false,
                    },
                },
                legend: {
                    navigation: {
                        enabled: false,
                    },
                    itemHoverStyle: {},
                },
                plotOptions: {
                    series: {
                        animation: false,
                        states: {
                            hover: {
                                enabled: false,
                            },
                        },
                        point: {
                            events: {
                                legendItemClick: () => false,
                            },
                        },
                        events: {
                            legendItemClick: () => false,
                        },
                    },
                },
                tooltip: {
                    enabled: false,
                },
            },
            dynamic: {
                ...UIChart._a11yConfig,
                chart: {
                    animation: true,
                    zooming: {
                        mouseWheel: false,
                    },
                },
                legend: {
                    navigation: {
                        enabled: false,
                    },
                    itemHoverStyle: {},
                },
                plotOptions: {
                    series: {
                        animation: true,
                        states: {
                            hover: {
                                enabled: true,
                            },
                        },
                        point: {
                            events: {
                                legendItemClick: () => false,
                            },
                        },
                        events: {
                            legendItemClick: () => false,
                        },
                    },
                },
                tooltip: {
                    enabled: false,
                },
            },
            interactive: {
                ...UIChart._a11yConfig,
                chart: {
                    animation: true,
                    zooming: {
                        mouseWheel: false,
                    },
                },
                legend: {
                    enabled: true,
                    navigation: {
                        enabled: true,
                    },
                },
                plotOptions: {
                    series: {
                        allowPointSelect: false,
                        animation: true,
                        states: {
                            hover: {
                                enabled: true,
                            },
                        },
                        point: {
                            events: {
                                legendItemClick: () => true,
                            },
                        },
                        events: {
                            legendItemClick: () => true,
                        },
                    },
                },
                tooltip: {
                    enabled: false,
                },
            },
        };
    }

    /**
     * Formats HTML legend items with value.
     * @param {string} name Legend item name.
     * @param {number} value Numeric value to display.
     * @private
     */
    formatLegendLabelsWithValues(name: string, value: number): string {
        return `
      <div>
        <div class="legend-with-value">${value.toFixed(2)}</div>
        <div class="legend-category-label">${name}</div>
      </div>`;
    }

    /**
     * Custom legend label formatter for legend with icons and without them.
     * @param {UIChart} current The chart instance.
     */
    static customLegendFormatter(
        this: LegendFormatterContext,
        current: UIChart
    ): string {
        let html: string | undefined;
        const options = current.getOptions();
        if (!options.series) {
            return '';
        }
        const series = options.series[0];
        const getIcon = (): string | null => {
            if (series.data?.[this.index]?.icon) {
                return series.data[this.index].icon!;
            }
            if (series.icon) {
                return series.icon;
            }
            return null;
        };

        const icon = getIcon();
        if (icon) {
            current.setAttribute('with-icons', 'true');
            // For pie charts `this` is a Point (legend item = slice); for other
            // chart types `this` is a Series. Only Series has `.chart` directly;
            // Points expose it via `.series.chart`.
            const hcChart =
                (this as unknown as { series?: { chart: Chart } }).series
                    ?.chart ?? (this as unknown as { chart: Chart }).chart;
            if (
                (
                    hcChart?.options.legend as LegendOptions & {
                        itemsWithValues?: boolean;
                    }
                )?.itemsWithValues
            ) {
                html = current.formatLegendLabelsWithValues(
                    this.name,
                    -(series.data?.[this.index]?.y ?? 0)
                );
            } else {
                html = this.name;
            }
            const className = series.data?.[this.index]?.className;
            const computedClass = className
                ? className
                : 'highcharts-color-' + (this.colorIndex ?? 0);
            const caption = createElement<UIElement>({
                tagName: 'ui-caption',
                attributes: {
                    layout: 'chip',
                    position: 'right',
                    color: computedClass,
                },
            })!;
            caption.setUnsafeInnerHTML(html ?? '');
            caption.insertElements([
                {
                    tagName: 'ui-icon',
                    attributes: {
                        glyph: icon,
                        size: 'medium',
                        class: computedClass,
                    },
                },
            ]);
            return caption.outerHTML;
        } else {
            return this.name;
        }
    }

    /**
     * Remap value from one range to another.
     * @param {number} value The value to remap.
     * @param {number} min1 Source range minimum.
     * @param {number} max1 Source range maximum.
     * @param {number} min2 Target range minimum.
     * @param {number} max2 Target range maximum.
     */
    static remap(
        value: number,
        min1: number,
        max1: number,
        min2: number,
        max2: number
    ): number {
        return ((value - min1) * (max2 - min2)) / (max1 - min1) + min2;
    }

    /**
     * Calculates coordinates on circle based on radius and angle.
     * Also sets small offsets to better position.
     * @param {number} radius Radius of a circle.
     * @param {number} [theta=0] Angle between X-axis and line.
     * @param {number} [offsetX=0] Relative horizontal offset applied to the radius.
     * @param {number} [offsetY=0] Relative vertical offset applied to the radius.
     */
    static calculateCircleCoords(
        radius: number,
        theta: number = 0,
        offsetX: number = 0,
        offsetY: number = 0
    ): { x: number; y: number } {
        return {
            x: (radius + radius * offsetX) * Math.cos(theta),
            y: (radius + radius * offsetY) * Math.sin(theta),
        };
    }

    /**
     * Calculates sum for given series.
     * @param {Series} series The Highcharts series instance.
     * @private
     */
    calculateSeriesSum(series: Series): number {
        return series.points.reduce(
            (sum: number, point: Point) => sum + (point.y ?? 0),
            0
        );
    }

    /**
     * Calculates percent of point value from given series.
     * NOTE: can be replaced in HC 11.1.0 with point.percentage.
     * @param {Series} series The Highcharts series instance.
     * @param {number} pointVal The point value to calculate percentage for.
     * @private
     */
    calculatePointPercentage(series: Series, pointVal: number): number {
        const sum = this.calculateSeriesSum(series);
        return sum ? (pointVal / sum) * 100 : 0;
    }

    /**
     * Create menu item with icon for exporting module.
     * Since 9 ver. of Highcharts cannot be used inside config.
     * @param {string} text Menu item label text.
     * @param {string} icon Icon glyph name.
     * @private
     */
    createExportingMenuItem(text: string, icon: string): string {
        const button = createElement<HTMLButtonElement>({
            tagName: 'button',
            classList: {
                '-iconed': true,
            },
            children: [
                {
                    tagName: 'ui-icon',
                    attributes: {
                        glyph: icon,
                        bgcolor: UIIcon.colors.TURQUOISE,
                    },
                },
            ],
        })!;
        return button.outerHTML + text;
    }

    /**
     * Creates container for chart.
     * @private
     */
    private createContainer(): HTMLElement {
        this._container = createElement<HTMLElement>({
            tagName: 'section',
            classList: {
                'ui-chart__container': true,
            },
        })!;
        this.appendChild(this._container);
        return this._container;
    }

    /**
     * Merges options from <ui-chart-options>...</ui-chart-options>.
     */
    mergeOptionsFromElement(): void {
        this.querySelectorAll('ui-chart-options').forEach((opt) => {
            // #bug: Safari renderer doesn't render element in tree deep.
            // So some nested components are rendered before parent or visa-versa.
            (
                opt as HTMLElement & {
                    type: string;
                    get: () => Options;
                }
            ).type = 'application/json';

            const json = (opt as HTMLElement & { get: () => Options }).get();
            this.setOptions(json);
        });
    }

    /**
     * Merges presets with options.
     * @private
     */
    protected resolvePresets(): boolean {
        if (!this.preset) {
            return false;
        }
        this.setOptions(UIChart.presets[this.preset]);
        return true;
    }

    /**
     * Sets custom the config for UIChart instance.
     * @param {Options} opts Highcharts options to merge in.
     */
    setOptions(opts: Options): void {
        this.opts = merge(this.opts, opts);
    }

    /**
     * Gets options for chart. Mostly is used for debugging purposes.
     */
    getOptions<T = UIChartExtendedOptions>(): T {
        return this.opts as T;
    }

    /**
     * Gets children series.
     */
    fetchSeries(): NodeListOf<Element> {
        return this.querySelectorAll('ui-series');
    }

    /**
     * Gets data from series objects.
     */
    getDataFromSeries(): void {
        const series = this.fetchSeries();
        if (series.length > 0) {
            this.opts.series = [];
        }
        series.forEach((ser) => {
            this.opts.series!.push(
                (
                    ser as HTMLElement & { toJSON(): Record<string, unknown> }
                ).toJSON() as unknown as SeriesOptionsType
            );
        });
    }

    /**
     * Adds an event listener. Wrapper function to Highcharts.
     * @param {string} type The event type.
     * @param {EventCallbackFunction} fn The function callback.
     * @param {EventOptionsObject} [options] Options for adding the event.
     */
    addEvent(
        type: string,
        fn:
            | EventCallbackFunction<Chart>
            | ((...args: Array<unknown>) => unknown),
        options?: EventOptionsObject
    ): void {
        addEvent(
            this.chart,
            type,
            fn as (...args: Array<unknown>) => unknown,
            options
        );
    }

    /**
     * Draws the chart.
     * @param {Highcharts.Options} [opts] Any possible options from Highcharts API.
     */
    draw(opts: Options = {}): void {
        const currentChart = this.instance;
        const title = currentChart.opts?.title?.text ?? '';
        this.setOptions({
            chart: {
                renderTo: this.container,
                type: this.type,
                styledMode: true,
                colorCount: UIChart.COLORS_COUNT,
                height: this.height,
            },
            exporting: {
                enabled: false,
                allowHTML: true,
                buttons: {
                    contextButton: {
                        enabled: true,
                        symbol: 'menuball',
                        menuItems: [
                            'downloadPNG',
                            'downloadJPEG',
                            'downloadPDF',
                            'downloadSVG',
                        ],
                    },
                },
                menuItemDefinitions: {
                    downloadPNG: {
                        text: 'PNG',
                    },
                    downloadJPEG: {
                        text: 'JPEG',
                    },
                    downloadSVG: {
                        text: 'SVG',
                    },
                    downloadPDF: {
                        text: 'PDF',
                    },
                },
            },
            title: {
                align: 'left',
                text: title,
            },
            legend: {
                enabled: false,
                useHTML: false,
                itemHoverStyle: {},
                navigation: {
                    enabled: false,
                },
                labelFormatter: function (this: Series | Point): string {
                    return UIChart.customLegendFormatter.call(
                        this as unknown as LegendFormatterContext,
                        currentChart
                    );
                },
            },
            credits: {
                enabled: false,
            },
            plotOptions: {
                series: {
                    animation: false,
                    states: {
                        hover: {
                            enabled: true,
                        },
                    },
                    point: {
                        events: {
                            legendItemClick: () => false,
                        },
                    },
                    events: {
                        legendItemClick: () => false,
                    },
                    allowPointSelect: false,
                },
            },
            tooltip: {
                enabled: false,
                valueSuffix: this.units,
            },
            xAxis: {
                visible: false,
                allowDecimals: false,
                labels: {
                    distance: 10,
                    padding: 5,
                },
            },
            yAxis: {
                visible: false,
                allowDecimals: false,
                minRange: 0.1,
            },
        });
        this.resolvePresets();
        this.mergeOptionsFromElement();
        this.getDataFromSeries();
        this.setOptions(opts);

        const dataOptions = this.getOptions().data;
        if (dataOptions) {
            if (dataOptions.csvURL) {
                this.setOptions({
                    data: {
                        csvURL: dataOptions.csvURL,
                    },
                });
            }
            if (dataOptions.rowsURL) {
                this.setOptions({
                    data: {
                        rowsURL: dataOptions.rowsURL,
                    },
                });
            }
        }

        this._chart = this.stock
            ? new Highcharts.StockChart(this.getOptions())
            : new Highcharts.Chart(this.getOptions());
    }

    /**
     * Redraws the chart.
     * @param {boolean} [redraw] Whether to return the redraw status.
     * @param {boolean | AnimationOptionsObject} [animation] Animation options.
     */
    redraw(
        redraw: boolean = true,
        animation: boolean | AnimationOptionsObject = true
    ): boolean {
        if (!this._chart) {
            return false;
        }
        this._chart.redraw(animation);
        return redraw;
    }

    /**
     * Main method for chart rendering. Every chart's type has its own
     * renderChart() method. This method should be overridden.
     */
    renderChart(): void {
        this.draw(this.getOptions());
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        // Charts should be rendered only when DOM is fully ready.
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        whenDOMReady(() => {
            this.renderChart();

            // Accessibility: we are using <section> as container, it means
            // we do not need additional aria role="region"
            // that is coming directly from Highcharts library
            this.container?.removeAttribute('role');
            if (!this.opts.title?.text) {
                this.container?.removeAttribute('aria-label');
            }
        }, false);
    }
}
